import { createFileRoute, Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useDashboardStats, useRustyPieces } from '@/lib/queries/insights';
import { usePracticeQueue } from '@/lib/queries/practice-queue';
import { useProfile } from '@/lib/queries/profile';
import { StatTile } from '@/components/molecules/stat-tile';
import { PieceRow } from '@/components/molecules/piece-row';
import { RustyItem } from '@/components/molecules/rusty-item';
import { MasteryMeter } from '@/components/molecules/mastery-meter';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { Micro } from '@/components/atoms/micro';
import { Icon } from '@/components/atoms/icon';
import { masteryVisualLevel, type MasteryVisualLevel } from '@/lib/mastery';
import { relativeDate } from '@/lib/relative-date';
import type { QueueItem, RustyPiece } from '@/lib/types';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

// ─── helpers ────────────────────────────────────────────────────────────────

// How many focus rows the dashboard shows on mobile. Overflow rows are
// rendered desktop-only via `hidden md:block`. The full list lives on
// /practice. Per design (screens-mobile.jsx).
const MOBILE_FOCUS_PREVIEW_LIMIT = 3;

const CANONICAL_TO_SORT: Readonly<Record<string, number>> = {
  not_started: 0,
  hard_to_sightread: 1,
  good_sightreading: 2,
  halfway_memorized: 3,
  memorized_with_articulation_dynamics: 4,
  performance_ready: 5,
};

function visualLevelFromName(name: string | null): MasteryVisualLevel {
  if (name == null) return 'new';
  return masteryVisualLevel(CANONICAL_TO_SORT[name] ?? null);
}

function formatWeeklyHours(
  _sessions: number | null | undefined,
  _avgMinutes: number | null | undefined,
): string {
  // TODO: backend endpoint — see BACKLOG.md "Dashboard / History aggregates have no endpoints"
  // No weekly-total-minutes endpoint exists yet; returning placeholder.
  return '—';
}

function buildGreeting(name: string | null | undefined): string {
  const firstName = name?.split(' ')[0] ?? null;
  const base = 'Good morning';
  return firstName ? `${base}, ${firstName}` : base;
}

function buildDateSubtitle(): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  }).format(new Date());
}

function mapQueueItemToPieceRow(item: QueueItem): ReactElement {
  return (
    <Link
      key={item.userArrangementId}
      to="/repertoire/$arrangementId"
      params={{ arrangementId: item.arrangementId }}
      className="block"
    >
      <PieceRow
        title={item.pieceName}
        composer={item.composerName ?? 'Traditional'}
        form={item.formName ?? '—'}
        musicalKey={item.keyName ?? '—'}
        mastery={masteryVisualLevel(item.masteryLevelSortOrder)}
        lastPracticed={relativeDate(item.daysSinceLastPracticed)}
        dueNow={item.dueNow}
      />
    </Link>
  );
}

function mapRustyPieceToRustyItem(rp: RustyPiece): ReactElement {
  return (
    <Link
      key={rp.userArrangementId}
      to="/repertoire/$arrangementId"
      params={{ arrangementId: rp.arrangementId }}
      className="block"
    >
      <RustyItem
        title={rp.pieceName}
        arrangement={rp.arrangementName}
        days={rp.daysSinceLastPracticed}
        mastery={visualLevelFromName(rp.masteryLevelName)}
        compact
      />
    </Link>
  );
}

// ─── Activity chart placeholder ──────────────────────────────────────────────

const ACTIVITY_VALUES = [18, 22, 0, 35, 44, 28, 30, 24, 32, 20, 48, 52, 38, 0, 28, 44, 38, 50, 62, 42, 30, 36, 52, 48, 55, 68, 50, 44] as const;
const ACTIVITY_MAX = 68;

function ActivityChartPlaceholder(): ReactElement {
  return (
    <div className="mt-6">
      <div className="flex items-end justify-between mb-3">
        <div>
          <Micro>Last four weeks</Micro>
          <div className="font-display text-[18px] font-semibold mt-1">Keep it going</div>
        </div>
      </div>
      {/* TODO: backend endpoint — see BACKLOG.md "Dashboard / History aggregates have no endpoints" */}
      <Card padding="lg">
        <div className="flex items-end gap-1.5 h-32">
          {ACTIVITY_VALUES.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col justify-end items-center gap-1">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${(v / ACTIVITY_MAX) * 100}%`,
                  background: v === 0 ? 'var(--rule)' : v > 40 ? 'var(--brand)' : 'var(--brand-wash)',
                  minHeight: v === 0 ? 4 : 6,
                }}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 mt-3 text-[10px] font-mono text-[var(--ink-3)] uppercase tracking-wider">
          <div>Week 13</div>
          <div>Week 14</div>
          <div>Week 15</div>
          <div>Week 16</div>
        </div>
      </Card>
    </div>
  );
}

// ─── Sidebar placeholder cards ───────────────────────────────────────────────

function SuggestedCard(): ReactElement {
  return (
    // TODO: backend endpoint — see BACKLOG.md "Dashboard / Suggested set has no endpoint"
    <Card padding="lg" className="bg-[var(--warm-wash)] border-[var(--warm)]/20">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[var(--warm)] text-white flex items-center justify-center shrink-0">
          <Icon name="sparkle" size={14} />
        </div>
        <div>
          <div className="font-display text-[15px] font-semibold">A set to explore</div>
          <p className="text-[12px] text-[var(--ink-2)] mt-1 leading-relaxed">
            Cooley's into The Wise Maid. Shared key, shared bar — they pair neatly.
          </p>
        </div>
      </div>
    </Card>
  );
}

function UpcomingSessionCard(): ReactElement {
  return (
    // TODO: backend endpoint — see BACKLOG.md "Dashboard / Upcoming session has no endpoint"
    <div>
      <Micro className="block mb-3">Upcoming session</Micro>
      <Card padding="md">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-[var(--r-sm)] bg-[var(--bg-soft)] flex flex-col items-center justify-center">
            <span className="font-mono text-[9px] text-[var(--ink-3)]">SAT</span>
            <span className="font-display text-[14px] font-semibold leading-none">21</span>
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold">Thomond Session</div>
            <div className="text-[11px] text-[var(--ink-3)]">Dolan's · 9 pm</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function DashboardPage(): ReactElement {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: queue, isLoading: queueLoading } = usePracticeQueue();
  const { data: rustyData } = useRustyPieces();
  const { data: profile } = useProfile();

  const focusPieces = queue?.focusPieces ?? [];
  const maintenancePieces = queue?.maintenancePieces ?? [];
  const rustyPieces = rustyData ?? [];
  const canStartPractice = !queueLoading && focusPieces.length > 0;

  const greeting = buildGreeting(profile?.name);
  const dateSubtitle = buildDateSubtitle();

  const streakDays = stats?.streakDays ?? 0;
  const weeklyHours = formatWeeklyHours(stats?.sessionsLast7Days, stats?.averageSessionDurationMinutes);
  const fluentCount = stats?.masteryBreakdown?.['performance_ready'] ?? 0;
  const totalRepertoire = stats?.totalRepertoirePieces ?? 0;
  const dueCount = focusPieces.length;
  const dueCountLabel = String(dueCount).padStart(2, '0');

  const estimatedMinutes = Math.round(
    focusPieces.length * (stats?.averageSessionDurationMinutes ?? 20),
  );

  return (
    <div className="flex flex-col min-h-screen bg-[var(--bg-page)]">
      {/* Mobile greeting — simpler inline header (MobileHeader organism is Task 22) */}
      <header className="md:hidden px-5 pt-6 pb-3">
        <Micro>{dateSubtitle}</Micro>
        <h1 className="font-display text-[28px] font-semibold tracking-tight mt-1 leading-tight">
          {greeting}
        </h1>
      </header>

      {/* Desktop / tablet top bar */}
      <header className="hidden md:flex h-16 px-8 items-center border-b border-[var(--rule)] bg-[var(--bg-surface)] shrink-0">
        <div className="flex-1">
          <Micro>{dateSubtitle}</Micro>
          <div className="font-display text-[20px] font-semibold tracking-tight">{greeting}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/library/contribute">
            <Button variant="soft" icon="plus">Contribute piece</Button>
          </Link>
          <Link
            to="/practice"
            aria-disabled={!canStartPractice}
            onClick={(e) => {
              if (!canStartPractice) e.preventDefault();
            }}
          >
            <Button variant="primary" icon="play" disabled={!canStartPractice}>
              Start practice
            </Button>
          </Link>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto nice-scroll px-5 md:px-8 py-6 md:py-8 pb-24 md:pb-8">
        <div className="max-w-[1100px] mx-auto grid grid-cols-12 gap-6">

          {/* Mobile: streak + this-week split card */}
          <div className="col-span-12 md:hidden">
            <Card padding="none" className="overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-[var(--rule)]">
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon name="flame" size={14} className="text-[var(--warm)]" />
                    <Micro>Streak</Micro>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-[32px] font-semibold leading-none tabular-nums">
                      {statsLoading ? '—' : String(streakDays)}
                    </span>
                    <span className="text-[12px] text-[var(--ink-3)]">days</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon name="clock" size={13} className="text-[var(--ink-3)]" />
                    <Micro>This week</Micro>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-[32px] font-semibold leading-none tabular-nums">
                      {statsLoading ? '—' : weeklyHours}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Desktop / tablet: stat tiles row */}
          <div className="col-span-12 hidden md:grid md:grid-cols-4 gap-4">
            <StatTile
              label="Streak"
              value={statsLoading ? '—' : String(streakDays)}
              unit="days"
              icon="flame"
              tone="warm"
            />
            <StatTile
              label="This week"
              value={statsLoading ? '—' : weeklyHours}
              icon="clock"
              tone="brand"
            />
            <StatTile
              label="Fluent"
              value={statsLoading ? '—' : String(fluentCount)}
              unit={`/${totalRepertoire}`}
              icon="star"
              tone="ok"
            />
            <StatTile
              label="Due today"
              value={statsLoading ? '—' : dueCountLabel}
              icon="signal"
            />
          </div>

          {/* Left column: focus queue + activity chart */}
          <div className="col-span-12 md:col-span-8 space-y-0">
            {/* Focus queue section */}
            <section>
              <div className="flex items-center justify-between mb-3 px-1">
                <div>
                  <Micro>Today's focus · {focusPieces.length} {focusPieces.length === 1 ? 'tune' : 'tunes'}</Micro>
                  <div className="font-display text-[18px] font-semibold text-[var(--ink)] mt-0.5">
                    Ready when you are
                  </div>
                </div>
              </div>

              {focusPieces.length > 0 ? (
                <>
                  <Card padding="none" className="overflow-hidden">
                    <div className="divide-y divide-[var(--rule)]">
                      {focusPieces.map((item, index) => (
                        <div
                          key={item.userArrangementId}
                          className={
                            index < MOBILE_FOCUS_PREVIEW_LIMIT
                              ? 'p-4 hover:bg-[var(--bg-page)] transition-colors'
                              : 'hidden md:block p-4 hover:bg-[var(--bg-page)] transition-colors'
                          }
                        >
                          {mapQueueItemToPieceRow(item)}
                        </div>
                      ))}
                    </div>
                    <div className="p-4 bg-[var(--bg-page)] border-t border-[var(--rule)] flex items-center justify-between">
                      <div className="text-[12px] text-[var(--ink-3)]">
                        Estimated session · {estimatedMinutes} minutes
                      </div>
                      <Link
                        to="/practice"
                        aria-disabled={!canStartPractice}
                        onClick={(e) => {
                          if (!canStartPractice) e.preventDefault();
                        }}
                        className="hidden md:inline-flex"
                      >
                        <Button variant="primary" size="md" icon="play" disabled={!canStartPractice}>
                          Start practice
                        </Button>
                      </Link>
                    </div>
                  </Card>
                </>
              ) : (
                <div className="rounded-[var(--r-md)] border border-dashed border-[var(--rule)] bg-[var(--bg-surface)] p-6 text-center">
                  <p className="text-sm text-[var(--ink-3)]">No pieces in your focus queue yet.</p>
                  <Link
                    to="/repertoire"
                    className="mt-3 inline-block text-xs font-bold text-[var(--brand)] hover:underline"
                  >
                    Add something from your repertoire →
                  </Link>
                </div>
              )}
            </section>

            {/* Maintenance section — pieces queued for upkeep, not focus.
                Only renders when the backend returns maintenance items. */}
            {maintenancePieces.length > 0 && (
              <section className="mt-6">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div>
                    <Micro>Maintenance · {maintenancePieces.length} {maintenancePieces.length === 1 ? 'tune' : 'tunes'}</Micro>
                    <div className="font-display text-[18px] font-semibold text-[var(--ink)] mt-0.5">
                      Maintenance
                    </div>
                  </div>
                </div>
                <Card padding="none" className="overflow-hidden">
                  <div className="divide-y divide-[var(--rule)]">
                    {maintenancePieces.map((item) => (
                      <div
                        key={item.userArrangementId}
                        className="p-4 hover:bg-[var(--bg-page)] transition-colors"
                      >
                        {mapQueueItemToPieceRow(item)}
                      </div>
                    ))}
                  </div>
                </Card>
              </section>
            )}

            {/* Weekly activity chart placeholder */}
            <ActivityChartPlaceholder />
          </div>

          {/* Right column: suggested + recently added + upcoming session */}
          <div className="col-span-12 md:col-span-4 space-y-5 mt-6 md:mt-0">
            <SuggestedCard />

            {/* Recently added */}
            <div>
              <Micro className="block mb-3">Recently added</Micro>
              <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
                {(queue?.focusPieces ?? []).slice(0, 4).map((item) => (
                  <Link
                    key={item.userArrangementId}
                    to="/repertoire/$arrangementId"
                    params={{ arrangementId: item.arrangementId }}
                    className="p-3 flex items-center gap-2.5 hover:bg-[var(--bg-page)] cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-[var(--r-sm)] bg-[var(--bg-soft)] flex items-center justify-center font-display font-semibold text-[13px]">
                      {item.pieceName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold truncate">{item.pieceName}</div>
                      <div className="text-[10px] text-[var(--ink-3)]">
                        {item.formName ?? '—'} · {item.keyName ?? '—'}
                      </div>
                    </div>
                    <MasteryMeter
                      level={masteryVisualLevel(item.masteryLevelSortOrder)}
                      showLabel={false}
                      size="sm"
                    />
                  </Link>
                ))}
              </Card>
            </div>

            <UpcomingSessionCard />
          </div>

          {/* Mobile-only: rusty pieces section */}
          <section className="col-span-12 md:hidden" aria-label="Getting rusty">
            <div className="flex items-center justify-between mb-2 px-1">
              <Micro>Getting rusty · {rustyPieces.length}</Micro>
              <Link
                to="/history"
                className="text-[11px] font-semibold text-[var(--ink-2)]"
              >
                See all
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {rustyPieces.map(mapRustyPieceToRustyItem)}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
